import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { Theme } from '../hooks/useTheme';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  inactivityTimeout: number;
  onInactivityTimeoutChange: (value: number) => void;
  hideInactivitySeconds: boolean;
  onHideInactivitySecondsChange: (value: boolean) => void;
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
}

const SettingsModal = React.memo<SettingsModalProps>(({
  isOpen,
  onClose,
  inactivityTimeout,
  onInactivityTimeoutChange,
  hideInactivitySeconds,
  onHideInactivitySecondsChange,
  theme,
  onThemeChange
}) => {
  const [localTimeout, setLocalTimeout] = useState<number>(inactivityTimeout);
  const [localHideSeconds, setLocalHideSeconds] = useState<boolean>(hideInactivitySeconds);
  const [localTheme, setLocalTheme] = useState<Theme>(theme);
  
  // Update local state when props change
  useEffect(() => {
    setLocalTimeout(inactivityTimeout);
    setLocalHideSeconds(hideInactivitySeconds);
    setLocalTheme(theme);
  }, [inactivityTimeout, hideInactivitySeconds, theme]);

  const handleInactivityTimeoutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setLocalTimeout(value);
  };
  
  const handleThemeChange = (newTheme: Theme) => {
    setLocalTheme(newTheme);
  };
  
  const handleSave = () => {
    onInactivityTimeoutChange(localTimeout);
    onHideInactivitySecondsChange(localHideSeconds);
    onThemeChange(localTheme);
    onClose();
  };
  
  // Get color based on timeout value
  const getTimeoutColor = () => {
    if (localTimeout <= 3) return 'text-red-500';
    if (localTimeout <= 6) return 'text-orange-500';
    return 'text-green-500';
  };
  
  // Get difficulty label based on timeout value
  const getDifficultyLabel = () => {
    if (localTimeout <= 3) return 'Challenging';
    if (localTimeout <= 6) return 'Standard';
    if (localTimeout <= 10) return 'Relaxed';
    return 'Very Relaxed';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Settings" size="md">
      <div className="space-y-8 py-2">
        {/* Theme Settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Theme</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Choose the visual style for your writing experience.
          </p>
          
          <div className="mt-4 grid grid-cols-2 gap-4">
            <button
              onClick={() => handleThemeChange('light')}
              className={`p-4 rounded-lg border-2 transition-all ${
                localTheme === 'light' 
                  ? 'border-primary' 
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="w-full h-16 mb-2 bg-white rounded shadow-sm"></div>
              <div className="text-center font-medium text-gray-900 dark:text-gray-100">Light</div>
            </button>
            
            <button
              onClick={() => handleThemeChange('dark')}
              className={`p-4 rounded-lg border-2 transition-all ${
                localTheme === 'dark' 
                  ? 'border-primary' 
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="w-full h-16 mb-2 bg-gray-800 rounded shadow-sm"></div>
              <div className="text-center font-medium text-gray-900 dark:text-gray-100">Dark</div>
            </button>
            
            <button
              onClick={() => handleThemeChange('cyberpunk')}
              className={`p-4 rounded-lg border-2 transition-all ${
                localTheme === 'cyberpunk' 
                  ? 'border-primary' 
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div 
                className="w-full h-16 mb-2 rounded shadow-sm"
                style={{ 
                  background: 'linear-gradient(135deg, #0b0b19 0%, #1a1a30 100%)',
                  border: '1px solid #00f0ff',
                  boxShadow: '0 0 8px rgba(0, 240, 255, 0.5)'
                }}
              ></div>
              <div className="text-center font-medium text-gray-900 dark:text-gray-100">Cyberpunk</div>
            </button>
            
            <button
              onClick={() => handleThemeChange('edge')}
              className={`p-4 rounded-lg border-2 transition-all ${
                localTheme === 'edge' 
                  ? 'border-primary' 
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div 
                className="w-full h-16 mb-2 rounded shadow-sm"
                style={{ 
                  background: 'linear-gradient(135deg, #0b0f1c 0%, #151a2b 100%)',
                  border: '1px solid #00eaff',
                  boxShadow: '0 0 12px rgba(255, 0, 110, 0.3)'
                }}
              >
                <div
                  className="h-full w-full opacity-60"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 0, 110, 0.4) 0%, rgba(255, 239, 0, 0.4) 100%)',
                    clipPath: 'polygon(0 70%, 100% 30%, 100% 100%, 0 100%)'
                  }}
                ></div>
              </div>
              <div className="text-center font-medium text-gray-900 dark:text-gray-100">Edgerunners</div>
            </button>
          </div>
        </div>
        
        {/* Inactivity Timer Settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Inactivity Timer</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Set how long you can be inactive before your text is deleted. Shorter times create more
            pressure to keep writing, while longer times are more forgiving.
          </p>
          
          <div className="mt-6 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <label htmlFor="inactivityTimeout" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Inactivity timeout
              </label>
              <span className={`text-lg font-bold ${getTimeoutColor()}`}>
                {localTimeout}s
              </span>
            </div>
            
            <div className="flex items-center mt-3">
              <span className="text-xs text-gray-500 w-20">Difficulty:</span>
              <span className={`text-sm font-medium ${getTimeoutColor()}`}>
                {getDifficultyLabel()}
              </span>
            </div>
            
            <input
              id="inactivityTimeout"
              type="range"
              min="1"
              max="15"
              step="1"
              value={localTimeout}
              onChange={handleInactivityTimeoutChange}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer mt-4"
            />
            
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>1s</span>
              <span>5s</span>
              <span>10s</span>
              <span>15s</span>
            </div>
            
            <p className="mt-3 text-xs text-gray-500 italic">
              {localTimeout <= 3 
                ? "Very challenging! Text will be deleted quickly if you pause."
                : localTimeout <= 6
                ? "Standard setting. Requires consistent writing but gives some thinking time."
                : localTimeout <= 10
                ? "More relaxed. Gives you time to think between sentences."
                : "Very forgiving. Allows for longer pauses between writing."
              }
            </p>
          </div>
        </div>
        
        {/* Hide Seconds Setting */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Display Options</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Customize what information is shown during your writing sessions.
          </p>
          
          <div className="mt-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="hideSeconds" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hide countdown seconds
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Hide the seconds remaining in the inactivity timer for a cleaner, less stressful experience
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLocalHideSeconds(!localHideSeconds)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  localHideSeconds ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    localHideSeconds ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
});

export default SettingsModal; 